"use client";

import React, { useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import ProgressBar from "./ProgressBar";
import { Globe, Users, MapPin } from "lucide-react";
import { getCountryUserPercentage, useCityCountryPercentage } from "@/hooks/useCommon";

// TopoJSON URL
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface GeoFeature {
  rsmKey: string;
  properties: {
    name?: string;
    admin?: string;
  };
}

interface CountryData {
  year: number;
  country_name: string;
  country_id: number;
  country_booking_count: number;
  percentage: string;
}

interface CityData {
  city_name: string;
  country_name: string;
  country_id: number;
  total_bookings: number;
  percentage: string;
  city_rank: number;
}

type TabType = "users" | "cities";

const WorldMap: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<string>("India");
  const [activeTab, setActiveTab] = useState<TabType>("users");
  
  // Fetch country user data
  const { 
    data: countryData, 
    isLoading: countryLoading, 
    error: countryError 
  } = getCountryUserPercentage(selectedCountry);
  
  // Fetch top cities for selected country
  const { 
    data: citiesData, 
    isLoading: citiesLoading, 
    error: citiesError 
  } = useCityCountryPercentage({ 
    limit: 5, 
    country_name: selectedCountry 
  });

  const handleCountryClick = (geo: GeoFeature) => {
    const countryName = geo.properties.name || geo.properties.admin || "Unknown";
    console.log("Selected country:", countryName);
    setSelectedCountry(countryName);
  };

  // Get the yearly data from API response
  const yearlyData = countryData?.data || [];
  const topCities = citiesData?.data || [];

  return (
    <div className="max-w-3xl mx-auto bg-white p-1">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="bg-blue-300 p-2 rounded-md">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-[14px]">User by Country</h1>
        </div>
        
        {/* Tabs */}
        <div className="flex border border-gray-300 rounded p-0.5 bg-gray-100 text-xs">
          <button
            className={`px-2 py-1 rounded-sm transition-colors ${
              activeTab === "users"
                ? "bg-white text-blue-600 font-medium"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveTab("users")}
          >
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              Users
            </div>
          </button>
          <button
            className={`px-2 py-1 rounded-sm transition-colors ${
              activeTab === "cities"
                ? "bg-white text-blue-600 font-medium"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveTab("cities")}
          >
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              Cities
            </div>
          </button>
        </div>
      </div>

      {/* Map Section */}
      <div className="border border-gray-300 mb-2">
        <ComposableMap
          projectionConfig={{ scale: 160 }}
          width={800}
          height={400}
          style={{ width: "100%", height: "170px" }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }: { geographies: GeoFeature[] }) =>
              geographies.map((geo) => {
                const countryName = geo.properties.name || geo.properties.admin || "Unknown";
                const isSelected = selectedCountry === countryName;
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => handleCountryClick(geo)}
                    style={{
                      default: { 
                        fill: isSelected ? "#2563EB" : "#E0E0E0", 
                        outline: "none" 
                      },
                      hover: {
                        fill: "#2563EB",
                        cursor: "pointer",
                        outline: "none",
                      },
                      pressed: { fill: "#1D4ED8", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {/* Country header with loading state */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-[15px] font-semibold">{selectedCountry}</h1>
        {(activeTab === "users" && countryLoading) && (
          <span className="text-xs text-blue-500">Loading users...</span>
        )}
        {(activeTab === "users" && countryError) && (
          <span className="text-xs text-red-500">Error loading user data</span>
        )}
        {(activeTab === "cities" && citiesLoading) && (
          <span className="text-xs text-blue-500">Loading cities...</span>
        )}
        {(activeTab === "cities" && citiesError) && (
          <span className="text-xs text-red-500">Error loading city data</span>
        )}
      </div>

      {/* Content based on active tab */}
      {activeTab === "users" ? (
        // Users Tab Content
        yearlyData.length > 0 ? (
          yearlyData.map((item: CountryData, idx: number) => (
            <div key={idx} className="flex flex-col px-1 py-[5px]">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-[500] text-gray-600">{item.year}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-gray-700 font-[500]">
                    {parseFloat(item.percentage).toFixed(2)}%
                  </span>
                  <span className="text-[11px] text-gray-500">
                    ({item.country_booking_count.toLocaleString()})
                  </span>
                </div>
              </div>
              <ProgressBar 
                progress={parseFloat(item.percentage)} 
                color="blue-500" 
              />
            </div>
          ))
        ) : (
          !countryLoading && (
            <div className="text-center py-2">
              <p className="text-xs text-gray-500">No user data available for {selectedCountry}</p>
            </div>
          )
        )
      ) : (
        // Cities Tab Content
        topCities.length > 0 ? (
          topCities.map((city: CityData) => (
            <div key={`${city.city_name}-${city.country_id}`} className="flex flex-col px-1 py-[5px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                    #{city.city_rank}
                  </span>
                  <span className="text-[13px] font-[500] text-gray-600">{city.city_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-gray-700 font-[500]">
                    {parseFloat(city.percentage).toFixed(2)}%
                  </span>
                  <span className="text-[11px] text-gray-500">
                    ({city.total_bookings.toLocaleString()})
                  </span>
                </div>
              </div>
              <ProgressBar 
                progress={parseFloat(city.percentage)} 
                color="green-500" 
              />
            </div>
          ))
        ) : (
          !citiesLoading && (
            <div className="text-center py-2">
              <p className="text-xs text-gray-500">No city data available for {selectedCountry}</p>
            </div>
          )
        )
      )}

      {/* Loading Skeletons */}
      {((activeTab === "users" && countryLoading) || (activeTab === "cities" && citiesLoading)) && (
        Array.from({ length: 5 }).map((_, idx) => (
          <div key={idx} className="flex flex-col px-1 py-[5px]">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-[500] text-gray-300">----</span>
              <span className="text-[13px] text-gray-300 font-[500]">--%</span>
            </div>
            <ProgressBar progress={0} color="gray-200" />
          </div>
        ))
      )}
    </div>
  );
};

export default WorldMap;





// "use client";

// import React, { useState } from "react";
// import { ComposableMap, Geographies, Geography } from "react-simple-maps";
// import ProgressBar from "./ProgressBar";
// import { Globe } from "lucide-react";
// import { getCountryUserPercentage, getCityCountryPercentage } from "@/hooks/useCommon";

// // TopoJSON URL
// const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// interface GeoFeature {
//   rsmKey: string;
//   properties: {
//     name?: string;
//     admin?: string;
//   };
// }

// interface CountryData {
//   year: number;
//   country_name: string;
//   country_id: number;
//   country_booking_count: number;
//   percentage: string;
// }

// const WorldMap: React.FC = () => {
//   const [country, setCountry] = useState<string>("India");
  
//   // Use the hook to fetch country data
//   const { data: countryData, isLoading, error } = getCountryUserPercentage(country);

//   const handleCountryClick = (geo: GeoFeature) => {
//     const countryName = geo.properties.name || geo.properties.admin || "Unknown";
//     console.log("Selected country:", countryName);
//     setCountry(countryName);
//   };

//   // Get the yearly data from API response
//   const yearlyData = countryData?.data || [];

//   return (
//     <div className="max-w-3xl mx-auto bg-white p-1">
//       <div className="flex items-center gap-3 mb-2">
//         <div className="bg-blue-300 p-2 rounded-md">
//           <Globe className="w-5 h-5 text-white" />
//         </div>
//         <h1 className="text-[14px]">User by Country</h1>
//       </div>
      
//       <div className="border border-gray-300 mb-2">
//         <ComposableMap
//           projectionConfig={{ scale: 160 }}
//           width={800}
//           height={400}
//           style={{ width: "100%", height: "170px" }}
//         >
//           <Geographies geography={geoUrl}>
//             {({ geographies }: { geographies: GeoFeature[] }) =>
//               geographies.map((geo) => {
//                 const countryName = geo.properties.name || geo.properties.admin || "Unknown";
//                 const isSelected = country === countryName;
                
//                 return (
//                   <Geography
//                     key={geo.rsmKey}
//                     geography={geo}
//                     onClick={() => handleCountryClick(geo as GeoFeature)}
//                     style={{
//                       default: { 
//                         fill: isSelected ? "#2563EB" : "#E0E0E0", 
//                         outline: "none" 
//                       },
//                       hover: {
//                         fill: "#2563EB",
//                         cursor: "pointer",
//                         outline: "none",
//                       },
//                       pressed: { fill: "#1D4ED8", outline: "none" },
//                     }}
//                   />
//                 );
//               })
//             }
//           </Geographies>
//         </ComposableMap>
//       </div>

//       {/* Country header with loading state */}
//       <div className="flex items-center justify-between mb-2">
//         <h1 className="text-[15px] font-semibold">{country}</h1>
//         {isLoading && (
//           <span className="text-xs text-blue-500">Loading...</span>
//         )}
//         {error && (
//           <span className="text-xs text-red-500">Error loading data</span>
//         )}
//       </div>

//       {/* Display yearly data from API */}
//       {yearlyData.length > 0 ? (
//         yearlyData.map((item: CountryData, idx: number) => (
//           <div key={idx} className="flex flex-col px-1 py-[5px]">
//             <div className="flex items-center justify-between">
//               <span className="text-[13px] font-[500] text-gray-600">{item.year}</span>
//               <div className="flex items-center gap-2">
//                 <span className="text-[13px] text-gray-700 font-[500]">
//                   {parseFloat(item.percentage).toFixed(2)}%
//                 </span>
//                 <span className="text-[11px] text-gray-500">
//                   ({item.country_booking_count.toLocaleString()})
//                 </span>
//               </div>
//             </div>
//             <ProgressBar 
//               progress={parseFloat(item.percentage)} 
//               color="blue-500" 
//             />
//           </div>
//         ))
//       ) : (
//         // Show loading skeleton or empty state
//         !isLoading && (
//           <div className="text-center py-4">
//             <p className="text-sm text-gray-500">No data available for {country}</p>
//           </div>
//         )
//       )}

//       {/* Show loading skeleton while fetching */}
//       {isLoading && (
//         Array.from({ length: 5 }).map((_, idx) => (
//           <div key={idx} className="flex flex-col px-1 py-[5px]">
//             <div className="flex items-center justify-between">
//               <span className="text-[13px] font-[500] text-gray-300">----</span>
//               <span className="text-[13px] text-gray-300 font-[500]">--%</span>
//             </div>
//             <ProgressBar progress={0} color="gray-200" />
//           </div>
//         ))
//       )}
//     </div>
//   );
// };

// export default WorldMap;


